import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useEntityList } from '@/hooks/useEntityList';
import { useEntityOperations } from '@/hooks/common/useEntityOperations';
import { Task, DbTask } from '@/types/entities';
import { mapDbTaskToTask, mapTaskToDbInsert, mapTaskUpdateToDbUpdate } from '@/shared/mappers/taskMappers';
import { useAuth } from '@/context/AuthContext';

// Экспортируем тип Task для использования в компонентах
export type { Task };

export interface TaskFilters {
  status?: string;
  type?: string;
  search?: string;
  category?: string;
}

const TABLE_NAME = 'tasks';
const QUERY_KEY = [TABLE_NAME];

export const useTasksUnified = (filters?: TaskFilters) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  const { 
    data: tasks, 
    isLoading: isListLoading, 
    error, 
    refetch 
  } = useEntityList<Task, typeof TABLE_NAME>({
    tableName: TABLE_NAME,
    queryKey: QUERY_KEY,
    dbEntityToEntityMapper: mapDbTaskToTask,
    filters: {
      status: filters?.status,
      type: filters?.type,
      category: filters?.category,
    },
  });

  const { 
    create, 
    update, 
    delete: deleteEntity, 
    loading: isMutating 
  } = useEntityOperations(TABLE_NAME, 'Задача');

  const invalidateQueries = () => {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  };

  const createTask = async (taskData: Omit<Task, 'id' | 'userId' | 'createdAt' | 'updatedAt'>) => {
    if (!user?.id) {
      throw new Error('Пользователь не авторизован: невозможно создать задачу');
    }
    const dbData = mapTaskToDbInsert({ ...taskData, userId: user.id });
    const result = await create(dbData);
    invalidateQueries();
    return mapDbTaskToTask(result);
  };

  const updateTask = async (taskId: string, taskData: Partial<Omit<Task, 'id' | 'userId' | 'createdAt' | 'updatedAt'>>) => {
    const dbData = mapTaskUpdateToDbUpdate(taskData);
    const result = await update(taskId, dbData);
    invalidateQueries();
    return mapDbTaskToTask(result);
  };

  const deleteTask = async (taskId: string) => {
    await deleteEntity(taskId);
    invalidateQueries();
  };

  const filteredTasks = useMemo(() => {
    if (!tasks) return [];
    if (!filters?.search) return tasks;
    
    const searchTerm = filters.search.toLowerCase();
    return tasks.filter(task => {
        const titleMatch = task.title.toLowerCase().includes(searchTerm);
        const descriptionMatch = task.description?.toLowerCase().includes(searchTerm) ?? false;
        return titleMatch || descriptionMatch;
    });
  }, [tasks, filters?.search]);

  return {
    tasks: filteredTasks,
    allTasks: tasks || [],
    isLoading: isListLoading || isMutating,
    loading: isListLoading || isMutating, // Добавляем loading для совместимости
    error,
    createTask,
    updateTask,
    deleteTask,
    refetch,
  };
};
